/**
 * Request ID utilities for distributed tracing
 *
 * Generates a unique request ID for each frontend request to enable
 * correlation and distributed tracing across frontend and backend services.
 *
 * The request ID is sent via the `x-kog-request-id` header and can be used
 * by backend services and Envoy Gateway to correlate logs, create traces,
 * and track requests across the system.
 */

import { startSpan } from '@/modules/sentry';

/**
 * Generates a random hex string of specified length
 */
function generateRandomHex(length: number): string {
  const bytes = new Uint8Array(length / 2);
  crypto.getRandomValues(bytes);
  return Array.from(bytes)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Generates a unique request ID for the frontend request
 *
 * This request ID is sent via the `x-kog-request-id` header to the backend.
 * The backend/Envoy Gateway can use this ID to:
 * - Correlate logs across services
 * - Create distributed traces with the same ID
 * - Track request flow from frontend to backend
 *
 * @returns Object containing the request ID for the custom header
 */
export function getTraceContext(): {
  requestId: string;
} {
  // Generate a new 128-bit request ID (32 hex characters)
  const requestId = generateRandomHex(32);

  return {
    requestId,
  };
}

/**
 * Creates a new child span for an HTTP request
 * This is useful for creating spans for outgoing API calls
 *
 * @param url - The URL being requested
 * @param method - The HTTP method (GET, POST, etc.)
 * @returns A Sentry span that should be finished after the request completes
 */
export function startHttpSpan(url: string, method: string) {
  return startSpan(
    {
      name: `${method} ${url}`,
      op: 'http.client',
      attributes: {
        'http.method': method,
        'http.url': url,
      },
    },
    span => span,
  );
}
